import {
  checkTotalUnread,
  fetchChatConversations,
  getConversationMessages,
  createDirectConversation,
  loadChatUser,
  sendMessage,
  updateLastReadMessage,
} from "@/api/messages"
import { finishSessionRequest } from "@/api/user/coaching/sessionRequests"
import { SESSION_REQUEST_STATUSES } from "@/constants/coaching"

const state = {
  collapsed: true,
  totalUnreadMessages: 0,
  conversationPage: 1,
  lastConversationPage: 0,
  conversations: [],
  conversationSearchText: "",
  messageMap: {},
  activeConversations: [],
  loadingConversations: false,
  maxChats: 1,
  chatUser: {
    broadcast_channel: "FAKE_PUSHER_ID",
  },
  showGoProModal: false,
  scheduleSessionMap: {},
  loadingOpenChat: false,
}

const getters = {
  collapsed: state => state.collapsed,
  totalUnreadMessages: state => state.totalUnreadMessages,
  conversationPage: state => state.conversationPage,
  lastConversationPage: state => state.lastConversationPage,
  conversations: state => state.conversations,
  conversationSearchText: state => state.conversationSearchText,
  messageMap: state => state.messageMap,
  loadingConversations: state => state.loadingConversations,
  activeConversations: state => state.activeConversations,
  maxChats: state => state.maximumChats,
  chatUser: state => state.chatUser,
  showGoProModal: state => state.showGoProModal,
  scheduleSessionMap: state => state.scheduleSessionMap,
  loadingOpenChat: state => state.loadingOpenChat,
}

const mutations = {
  setCollapsed: (state, collapsed) => (state.collapsed = collapsed),
  setTotalUnreadMessages: (state, totalUnreadMessages) =>
    (state.totalUnreadMessages = totalUnreadMessages),
  setConversationPage: (state, conversationPage) =>
    (state.conversationPage = conversationPage),
  setLastConversationPage: (state, lastConversationPage) =>
    (state.lastConversationPage = lastConversationPage),
  setConversations: (state, conversations) =>
    (state.conversations = [...conversations]),
  setConversationSearchText: (state, text) =>
    (state.conversationSearchText = text),
  setMessageMap: (state, messages) => (state.messageMap = { ...messages }),
  setLoadingConversations: (state, loadingConversations) =>
    (state.loadingConversations = loadingConversations),
  setActiveConversations: (state, conversations) =>
    (state.activeConversations = conversations),
  setMaxChats: (state, maxChats) => (state.maxChats = maxChats),
  setChatUser: (state, chatUser) => (state.chatUser = chatUser),
  setShowGoProModal: (state, show) => (state.showGoProModal = show),
  setLoadingOpenChat: (state, bol) => (state.loadingOpenChat = bol),
  setScheduleSessionMap: (state, scheduleSessionMap) =>
    (state.scheduleSessionMap = { ...scheduleSessionMap }),
}

const actions = {
  async checkTotalUnread ({ commit }) {
    const data = await checkTotalUnread()
    if (data && data.success) {
      commit("setTotalUnreadMessages", data.result)
    }
  },
  async fetchChatConversations ({ commit, state, dispatch }) {
    if (state.conversations.length) {
      return
    }
    commit("setLoadingConversations", true)
    const data = await fetchChatConversations(state.conversationPage)
    if (data && data.success) {
      commit("setConversationPage", state.conversationPage + 1)
      commit("setConversations", data.result.data)
      commit("setLastConversationPage", data.result.last_page)
    }
    commit("setLoadingConversations", false)
    dispatch("checkConversationInSession")
  },
  async checkConversationInSession ({ commit, dispatch, rootGetters, state }) {
    await dispatch("Coaching/loadActiveSessionRequest", null, { root: true })
    const activeRequest = rootGetters["Coaching/activeSessionRequest"]
    const coachPgID =
      activeRequest &&
      [
        SESSION_REQUEST_STATUSES.ACCEPTED,
        SESSION_REQUEST_STATUSES.IN_SESSION,
      ].includes(activeRequest.status)
        ? activeRequest.coach.user.pg_id
        : ""
    const conversations = state.conversations.map(conversation => {
      const users = conversation.users
      for (let user of users) {
        if (user.index.toString() === coachPgID) {
          conversation.inSession = activeRequest
          dispatch("openChat", conversation)
          break
        } else {
          conversation.inSession = null
        }
      }
      return conversation
    })
    commit("setConversations", conversations)
  },
  async fetchMoreChatConversations ({ commit, state }) {
    // if (state.loadingConversations) {
    //   return
    // }
    commit("setLoadingConversations", true)
    const data = await fetchChatConversations(state.conversationPage)
    if (data && data.success) {
      commit("setConversationPage", state.conversationPage + 1)
      commit("setConversations", [...state.conversations, ...data.result.data])
    }
    commit("setLoadingConversations", false)
  },
  async getConversationMessages ({ commit, state }, payload) {
    const data = await getConversationMessages(payload)
    const messageMap = state.messageMap
    const conversationID = payload.conversation.id
    messageMap[conversationID] = data.result.data.reverse()
    commit("setMessageMap", messageMap)
  },
  async getMoreConversationMessages ({ commit, state }, payload) {
    const data = await getConversationMessages(payload)
    const messageMap = state.messageMap
    const conversationID = payload.conversation.id
    messageMap[conversationID] = [
      ...data.result.data.reverse(),
      ...state.messageMap[conversationID],
    ]
    commit("setMessageMap", messageMap)
  },
  async openChat ({ commit, state, dispatch }, conversation) {
    // const conversationID = conversation.id
    let activeConversations = state.activeConversations
    let maxChats = state.maxChats
    for (let activeConversation of activeConversations) {
      if (activeConversation.id === conversation.id) {
        return
      }
    }
    const innerWidth = window.innerWidth
    if (innerWidth > 992) {
      const chatSpaces = innerWidth - 650
      maxChats = Math.floor(chatSpaces / 370)
      commit("setMaxChats", maxChats)
    } else {
      commit("setMaxChats", 1)
    }
    activeConversations.unshift(conversation)
    if (activeConversations.length > maxChats) {
      activeConversations = activeConversations.slice(0, maxChats)
    }
    commit("setActiveConversations", activeConversations)
  },
  removeConversation ({ commit, state }, conversation) {
    const conversations = state.activeConversations.filter(
      activeConversation => activeConversation.id !== conversation.id
    )
    commit("setActiveConversations", conversations)
  },
  async updateLastReadMessage (
    { commit, state, dispatch },
    { conversationId, messageId }
  ) {
    if (messageId) {
      await updateLastReadMessage(conversationId, messageId)
    }
    const conversations = state.conversations.map(conversation => {
      if (conversation.id === conversationId) {
        conversation.unread_count = 0
      }
      return conversation
    })
    commit("setConversations", conversations)
    dispatch("checkTotalUnread")
  },
  async sendMessage ({ commit, state, dispatch }, { conversationId, message }) {
    dispatch("updateLastReadMessage", { conversationId })
    const messageMap = state.messageMap
    const messages = messageMap[conversationId]
    messages.push(message)
    messageMap[conversationId] = messages
    await commit("setMessageMap", messageMap)
    await sendMessage(conversationId, message)
  },
  async loadChatUser ({ commit, state }) {
    if (state.chatUser.broadcast_channel !== "FAKE_PUSHER_ID") return
    const data = await loadChatUser()
    if (data && data.success) {
      commit("setChatUser", data.result)
    }
  },
  async openDirectConversation (
    { commit, state, dispatch, rootGetters },
    { user, forceOpen }
  ) {
    commit("setLoadingOpenChat", true)
    if (state.chatUser.index.toString() === user) {
      commit("setLoadingOpenChat", false)
      return
    }
    const conversations = state.conversations
    for (let conversation of conversations) {
      const users = conversation.users
      for (let conversationUser of users) {
        if (conversationUser.index.toString() === user) {
          await dispatch("openChat", conversation)
          commit("setLoadingOpenChat", false)
          return
        }
      }
    }

    // const isUserPro = rootGetters["User/isUserPro"]
    // if (isUserPro || forceOpen) {
    await dispatch("loadChatUser")
    const data = await createDirectConversation(state.chatUser.index, user)
    const conversation = data.result
    conversations.unshift(conversation)
    commit("setConversations", conversations)
    dispatch("openChat", conversation)
    // } else {
    //   commit("setShowGoProModal", true)
    // }
    commit("setLoadingOpenChat", false)
  },
  async finishSessionRequest ({ commit, state, rootGetters }, sessionId) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    finishSessionRequest(instaProAccessToken, sessionId)
    const conversations = state.conversations.map(conversation => {
      if (conversation.inSession && conversation.inSession.id === sessionId) {
        conversation.inSession = null
      }
      return conversation
    })
    commit("setConversations", conversations)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/message.js